import { useState, useEffect, useRef } from 'react';
import { useToast } from '../contexts/ToastContext';
import opportunityService from '../services/opportunityService';

const useOpportunityNotifications = (enabled = true, minConfidence = 0.8) => {
  const [lastCheck, setLastCheck] = useState(Date.now());
  const [isMonitoring, setIsMonitoring] = useState(false);
  const intervalRef = useRef(null);
  const { showToast } = useToast();

  const checkForNewOpportunities = async () => {
    try {
      const response = await opportunityService.getActiveOpportunities();
      
      if (response.success) {
        const highConfidenceOpportunities = response.data.opportunities.filter(
          opp => opp.confidence_score >= minConfidence && 
                 new Date(opp.created_at) > new Date(lastCheck)
        );

        // Show notifications for new high-confidence opportunities
        highConfidenceOpportunities.forEach(opportunity => {
          const opportunityType = opportunity.opportunity_type.replace('_', ' ').toUpperCase();
          const confidence = (opportunity.confidence_score * 100).toFixed(0);
          
          showToast({
            type: 'success',
            title: `🚀 New Trading Opportunity!`,
            message: `${opportunity.symbol || 'Token'}: ${opportunityType} detected with ${confidence}% confidence`,
            duration: 8000,
            action: {
              label: 'View Details',
              onClick: () => {
                // Navigate to opportunities page
                window.location.hash = '#opportunities';
              }
            }
          });
        });

        setLastCheck(Date.now());
        return highConfidenceOpportunities.length;
      }
    } catch (error) {
      console.error('Error checking for new opportunities:', error);
    }
    
    return 0;
  };

  const startMonitoring = () => {
    if (!enabled || isMonitoring) return;

    setIsMonitoring(true);
    setLastCheck(Date.now());

    // Check every 5 minutes
    intervalRef.current = setInterval(checkForNewOpportunities, 5 * 60 * 1000);
  };

  const stopMonitoring = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsMonitoring(false);
  };

  useEffect(() => {
    if (enabled) {
      startMonitoring();
    }

    return () => {
      stopMonitoring();
    };
  }, [enabled, minConfidence]);

  return {
    isMonitoring,
    startMonitoring,
    stopMonitoring,
    checkForNewOpportunities
  };
};

export default useOpportunityNotifications;